import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ChevronLeft, ChevronRight, X } from 'lucide-react';
import authFetch from '../../utils/authFetch';
import { BookingForm, bookingAdults, bookingChildren, formatGuests, formatPaid, formatStatus, statusBadgeStyle } from './BookingsPage';
import { PropertyBadge, PropertyFilter, STR_PROPERTIES, getStrProperty, strBasePath, strPropertyQuery, useStrProperty } from './properties';

const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
const API_BASE = isLocal ? 'http://localhost:5000' : 'https://bdinteldb.com';

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const WEEKDAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const STATUS_COLORS = {
  new: { bg: '#dbeafe', color: '#1d4ed8', border: '#93c5fd' },
  confirmed: { bg: '#dcfce7', color: '#15803d', border: '#86efac' },
  declined: { bg: '#fee2e2', color: '#b91c1c', border: '#fca5a5' },
  cancelled: { bg: '#f1f5f9', color: '#64748b', border: '#cbd5e1' },
  pending_payment: { bg: '#fef3c7', color: '#b45309', border: '#fcd34d' }
};

const parseIsoDate = (iso) => {
  const [y, m, d] = String(iso).slice(0, 10).split('-').map(Number);
  return new Date(y, m - 1, d);
};

const toIsoDate = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const formatDateEu = (isoDate) => {
  if (!isoDate) return '';
  const [year, month, day] = String(isoDate).slice(0, 10).split('-');
  if (!year || !month || !day) return String(isoDate);
  return `${Number(day)}.${Number(month)}.${year}`;
};

const formatPrice = (value) => `${Number(value).toLocaleString('sl-SI', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} €`;

const EMPTY_FEES = { cleaningFee: null, petFee: null, touristTaxAdult: null, touristTaxChild: null };

const PlanningPage = () => {
  const navigate = useNavigate();
  // Set on a property's own page; null on the combined page, which adds the filter and property colours.
  const property = useStrProperty();
  const fixedKey = property?.key;
  const [propertyFilter, setPropertyFilter] = useState('all');
  const activeKey = fixedKey || propertyFilter;
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const today = useMemo(() => new Date(), []);
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth()); // 0-indexed

  const [selectedBooking, setSelectedBooking] = useState(null);
  // Keyed by property: the two properties have separate prices and fees.
  const [pricesByProperty, setPricesByProperty] = useState({});
  const [feesByProperty, setFeesByProperty] = useState({});
  const prices = pricesByProperty[activeKey] || {};

  useEffect(() => {
    const loadBookings = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await authFetch(`${API_BASE}/api/short-term-rentals/bookings${strPropertyQuery(fixedKey)}`);
        if (!response.ok) throw new Error('Failed to load bookings');
        const data = await response.json();
        setBookings(data.bookings || []);
      } catch (err) {
        setError(err.message || 'Failed to load bookings');
      } finally {
        setLoading(false);
      }
    };
    loadBookings();
  }, [fixedKey]);

  useEffect(() => {
    const loadPrices = async (key) => {
      const response = await authFetch(`${API_BASE}/api/short-term-rentals/prices${strPropertyQuery(key)}`);
      if (!response.ok) throw new Error('Failed to load prices');
      const data = await response.json();
      const map = {};
      (data.prices || []).forEach((p) => { map[p.price_date] = p.price; });
      setPricesByProperty((prev) => ({ ...prev, [key]: map }));
      setFeesByProperty((prev) => ({
        ...prev,
        [key]: {
          cleaningFee: data.fees?.cleaningFee ?? null,
          petFee: data.fees?.petFee ?? null,
          touristTaxAdult: data.fees?.touristTaxAdult ?? null,
          touristTaxChild: data.fees?.touristTaxChild ?? null
        }
      }));
    };
    const keys = fixedKey ? [fixedKey] : STR_PROPERTIES.map((p) => p.key);
    Promise.all(keys.map(loadPrices)).catch((err) => setError(err.message || 'Failed to load prices'));
  }, [fixedKey]);

  const handleBookingSaved = (updated) => {
    setBookings((prev) => prev.map((b) => (b.id === updated.id ? updated : b)));
    setSelectedBooking(updated);
  };

  const goToPrevMonth = () => {
    if (viewMonth === 0) {
      setViewMonth(11);
      setViewYear((y) => y - 1);
    } else {
      setViewMonth((m) => m - 1);
    }
  };

  const goToNextMonth = () => {
    if (viewMonth === 11) {
      setViewMonth(0);
      setViewYear((y) => y + 1);
    } else {
      setViewMonth((m) => m + 1);
    }
  };

  const goToToday = () => {
    setViewYear(today.getFullYear());
    setViewMonth(today.getMonth());
  };

  // Build map of ISO date -> bookings that occupy that night (check_in inclusive, check_out exclusive)
  const bookingsByDate = useMemo(() => {
    const map = {};
    // Expired = abandoned website checkouts; they never held the dates for long, so keep them off the calendar.
    bookings.filter((b) => b.status !== 'expired' && (activeKey === 'all' || b.property_key === activeKey)).forEach((b) => {
      const start = parseIsoDate(b.check_in);
      const end = parseIsoDate(b.check_out);
      const cursor = new Date(start);
      while (cursor < end) {
        const iso = toIsoDate(cursor);
        if (!map[iso]) map[iso] = [];
        map[iso].push(b);
        cursor.setDate(cursor.getDate() + 1);
      }
    });
    return map;
  }, [bookings, activeKey]);

  const calendarCells = useMemo(() => {
    const firstOfMonth = new Date(viewYear, viewMonth, 1);
    // Monday-first week: convert JS getDay() (0=Sun) to Monday-first index
    const firstWeekday = (firstOfMonth.getDay() + 6) % 7;
    const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();

    const cells = [];
    for (let i = 0; i < firstWeekday; i += 1) {
      cells.push(null);
    }
    for (let day = 1; day <= daysInMonth; day += 1) {
      cells.push(new Date(viewYear, viewMonth, day));
    }
    while (cells.length % 7 !== 0) {
      cells.push(null);
    }
    return cells;
  }, [viewYear, viewMonth]);

  const isToday = (date) => date && toIsoDate(date) === toIsoDate(today);

  return (
    <div>
      <button onClick={() => navigate(strBasePath(property))} style={backButtonStyle}>
        <ArrowLeft size={16} /> Back
      </button>

      <h1 style={{ color: '#1e293b', marginBottom: '6px' }}>Planning</h1>
      <p style={{ color: '#64748b', marginBottom: '30px' }}>
        {property ? `Calendar of all reservations for ${property.siteName}.` : 'Calendar of all reservations of all properties.'}
      </p>

      {!property && (
        <div style={{ marginBottom: '20px' }}>
          <PropertyFilter value={propertyFilter} onChange={setPropertyFilter} />
        </div>
      )}

      <div style={cardStyle}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px', flexWrap: 'wrap', gap: '10px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <button onClick={goToPrevMonth} style={navButtonStyle}><ChevronLeft size={18} /></button>
            <h2 style={{ margin: 0, color: '#1e293b', fontSize: '18px', minWidth: '160px', textAlign: 'center' }}>
              {MONTH_NAMES[viewMonth]} {viewYear}
            </h2>
            <button onClick={goToNextMonth} style={navButtonStyle}><ChevronRight size={18} /></button>
          </div>
          <button onClick={goToToday} style={todayButtonStyle}>Today</button>
        </div>

        <div style={{ display: 'flex', gap: '14px', marginBottom: '16px', flexWrap: 'wrap', fontSize: '12px' }}>
          {Object.entries(STATUS_COLORS).map(([status, colors]) => (
            <div key={status} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ width: '10px', height: '10px', borderRadius: '3px', backgroundColor: colors.bg, border: `1px solid ${colors.border}`, display: 'inline-block' }} />
              <span style={{ color: '#64748b', textTransform: 'capitalize' }}>{formatStatus(status)}</span>
            </div>
          ))}
          {activeKey === 'all' && STR_PROPERTIES.map((p) => (
            <div key={p.key} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ width: '4px', height: '12px', borderRadius: '2px', backgroundColor: p.color, display: 'inline-block' }} />
              <span style={{ color: '#64748b' }}>{p.name}</span>
            </div>
          ))}
        </div>

        {loading ? (
          <p style={{ color: '#64748b' }}>Loading...</p>
        ) : error ? (
          <p style={{ color: '#dc2626' }}>{error}</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <div style={{ minWidth: '700px' }}>
              <div style={weekHeaderRowStyle}>
                {WEEKDAY_NAMES.map((wd) => (
                  <div key={wd} style={weekHeaderCellStyle}>{wd}</div>
                ))}
              </div>
              <div style={gridStyle}>
                {calendarCells.map((date, idx) => {
                  if (!date) {
                    return <div key={`empty-${idx}`} style={emptyCellStyle} />;
                  }
                  const iso = toIsoDate(date);
                  const dayBookings = bookingsByDate[iso] || [];
                  return (
                    <div key={iso} style={{ ...dayCellStyle, ...(isToday(date) ? todayCellStyle : {}) }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '4px' }}>
                        <span style={dayNumberStyle}>{date.getDate()}</span>
                        {prices[iso] !== undefined && <span style={dayPriceStyle}>{formatPrice(prices[iso])}</span>}
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                        {dayBookings.map((b) => {
                          const colors = STATUS_COLORS[b.status] || STATUS_COLORS.new;
                          return (
                            <div
                              key={b.id}
                              role="button"
                              tabIndex={0}
                              onClick={() => setSelectedBooking(b)}
                              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelectedBooking(b); } }}
                              title={`${property ? '' : `${getStrProperty(b.property_key)?.name}: `}${b.full_name} (${formatDateEu(b.check_in)} - ${formatDateEu(b.check_out)}) - ${formatGuests(b)} guests - ${formatStatus(b.status)}`}
                              style={{
                                backgroundColor: colors.bg,
                                color: colors.color,
                                border: `1px solid ${colors.border}`,
                                ...(activeKey === 'all' ? { borderLeft: `4px solid ${getStrProperty(b.property_key)?.color || colors.border}` } : {}),
                                borderRadius: '4px',
                                padding: '2px 5px',
                                fontSize: '11px',
                                cursor: 'pointer',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              {b.full_name}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>

      {selectedBooking && (
        <BookingDetailsModal
          booking={selectedBooking}
          onClose={() => setSelectedBooking(null)}
          onSaved={handleBookingSaved}
          prices={pricesByProperty[selectedBooking.property_key] || {}}
          fees={feesByProperty[selectedBooking.property_key] || EMPTY_FEES}
          showProperty={!property}
        />
      )}
    </div>
  );
};

const countNights = (checkIn, checkOut) => Math.round((parseIsoDate(checkOut) - parseIsoDate(checkIn)) / 86400000);

// Sums nightly prices for check_in (inclusive) to check_out (exclusive) and adds the cleaning fee (and pet fee when a pet comes).
const calculateStayPrice = (booking, prices, fees) => {
  const end = parseIsoDate(booking.check_out);
  const cursor = parseIsoDate(booking.check_in);
  let nightsTotal = 0;
  let missingNights = 0;
  let nights = 0;
  while (cursor < end) {
    const price = prices[toIsoDate(cursor)];
    if (price === undefined) missingNights += 1;
    else nightsTotal += price;
    nights += 1;
    cursor.setDate(cursor.getDate() + 1);
  }
  const cleaningFee = fees.cleaningFee || 0;
  const petFee = booking.has_pet ? (fees.petFee || 0) : 0;
  // Tourist tax is charged per person per night.
  const touristTax = nights * (bookingAdults(booking) * (fees.touristTaxAdult || 0) + bookingChildren(booking) * (fees.touristTaxChild || 0));
  return { nightsTotal, missingNights, cleaningFee, petFee, touristTax, total: nightsTotal + cleaningFee + petFee + touristTax };
};

const BookingDetailsModal = ({ booking, onClose, onSaved, prices, fees, showProperty }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const handleKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose]);

  const startEdit = () => {
    setDraft({
      checkIn: booking.check_in,
      checkOut: booking.check_out,
      adults: bookingAdults(booking),
      children: bookingChildren(booking),
      fullName: booking.full_name,
      email: booking.email || '',
      phone: booking.phone || '',
      message: booking.message || '',
      comments: booking.comments || '',
      hasPet: Boolean(booking.has_pet),
      status: booking.status
    });
    setError('');
    setEditing(true);
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const response = await authFetch(`${API_BASE}/api/short-term-rentals/bookings/${booking.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(draft)
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'Failed to update booking');
      onSaved(data.booking);
      setEditing(false);
    } catch (err) {
      setError(err.message || 'Failed to update booking');
    } finally {
      setSaving(false);
    }
  };

  const nights = countNights(booking.check_in, booking.check_out);
  const stayPrice = calculateStayPrice(booking, prices, fees);

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px', marginBottom: '18px' }}>
          <div>
            <h2 style={{ margin: '0 0 6px', color: '#1e293b', fontSize: '20px' }}>{booking.full_name}</h2>
            <span style={statusBadgeStyle(booking.status)}>{formatStatus(booking.status)}</span>
          </div>
          <button onClick={onClose} style={closeButtonStyle} aria-label="Close"><X size={18} /></button>
        </div>

        {error && <p style={{ color: '#dc2626', marginTop: 0 }}>{error}</p>}

        {editing ? (
          <>
            <BookingForm draft={draft} setDraft={setDraft} />
            <div style={{ display: 'flex', gap: '10px', marginTop: '16px' }}>
              <button onClick={handleSave} disabled={saving} style={saveButtonStyle}>{saving ? 'Saving...' : 'Save'}</button>
              <button onClick={() => setEditing(false)} style={secondaryButtonStyle}>Cancel</button>
            </div>
          </>
        ) : (
          <>
            <div style={detailsGridStyle}>
              {showProperty && <DetailItem label="Property" value={<PropertyBadge propertyKey={booking.property_key} />} />}
              <DetailItem label="Check-in" value={formatDateEu(booking.check_in)} />
              <DetailItem label="Check-out" value={formatDateEu(booking.check_out)} />
              <DetailItem label="Nights" value={nights} />
              <DetailItem label="Adults" value={bookingAdults(booking)} />
              <DetailItem label="Children" value={bookingChildren(booking)} />
              <DetailItem label="Pet" value={booking.has_pet ? 'Yes' : 'No'} />
              <DetailItem label="Email" value={booking.email ? <a href={`mailto:${booking.email}`} style={linkStyle}>{booking.email}</a> : '-'} />
              <DetailItem label="Phone" value={booking.phone ? <a href={`tel:${booking.phone}`} style={linkStyle}>{booking.phone}</a> : '-'} />
              <DetailItem label="Source" value={booking.source || '-'} capitalize />
              <DetailItem label="Received" value={formatDateEu(booking.created_at)} />
              {booking.amount_paid_cents ? <DetailItem label="Payment" value={`${formatPaid(booking)} (Stripe)`} /> : null}
            </div>
            <div style={priceBoxStyle}>
              <div style={detailLabelStyle}>Stay price</div>
              <div style={priceRowStyle}><span>{nights} night(s)</span><span>{formatPrice(stayPrice.nightsTotal)}</span></div>
              <div style={priceRowStyle}><span>Cleaning fee</span><span>{formatPrice(stayPrice.cleaningFee)}</span></div>
              {booking.has_pet && (
                <div style={priceRowStyle}><span>Pet fee</span><span>{formatPrice(stayPrice.petFee)}</span></div>
              )}
              {stayPrice.touristTax > 0 && (
                <div style={priceRowStyle}><span>Tourist tax</span><span>{formatPrice(stayPrice.touristTax)}</span></div>
              )}
              <div style={{ ...priceRowStyle, fontWeight: 700, borderTop: '1px solid #e2e8f0', paddingTop: '6px', marginTop: '4px' }}>
                <span>Total</span><span>{formatPrice(stayPrice.total)}</span>
              </div>
              {stayPrice.missingNights > 0 && (
                <div style={{ ...priceNoteStyle, color: '#b45309' }}>{stayPrice.missingNights} night(s) have no price set and are not included.</div>
              )}
            </div>
            <DetailItem label="Message" value={booking.message || '-'} block />
            <DetailItem label="Comments (internal)" value={booking.comments || '-'} block />
            <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
              <button onClick={startEdit} style={primaryButtonStyle}>Edit</button>
              <button onClick={onClose} style={secondaryButtonStyle}>Close</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const DetailItem = ({ label, value, block = false, capitalize = false }) => (
  <div style={block ? { marginTop: '14px' } : undefined}>
    <div style={detailLabelStyle}>{label}</div>
    <div style={{ color: '#1e293b', fontSize: '14px', whiteSpace: block ? 'pre-wrap' : 'normal', wordBreak: 'break-word', textTransform: capitalize ? 'capitalize' : 'none' }}>{value}</div>
  </div>
);

const backButtonStyle = { display: 'inline-flex', alignItems: 'center', gap: '6px', background: 'none', border: 'none', color: '#2563eb', cursor: 'pointer', fontSize: '14px', marginBottom: '20px', padding: 0 };
const cardStyle = { backgroundColor: 'white', padding: '25px', borderRadius: '16px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)', border: '1px solid #e2e8f0' };
const navButtonStyle = { display: 'flex', alignItems: 'center', justifyContent: 'center', width: '32px', height: '32px', borderRadius: '8px', border: '1px solid #e2e8f0', backgroundColor: 'white', cursor: 'pointer', color: '#334155' };
const todayButtonStyle = { padding: '6px 14px', borderRadius: '8px', border: '1px solid #e2e8f0', backgroundColor: 'white', cursor: 'pointer', color: '#334155', fontSize: '13px' };
const weekHeaderRowStyle = { display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', marginBottom: '6px' };
const weekHeaderCellStyle = { textAlign: 'center', fontSize: '12px', fontWeight: 600, color: '#64748b', padding: '6px 0' };
const gridStyle = { display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '6px' };
const dayCellStyle = { minHeight: '90px', border: '1px solid #f1f5f9', borderRadius: '8px', padding: '6px', backgroundColor: '#fafafa', display: 'flex', flexDirection: 'column', gap: '4px' };
const todayCellStyle = { border: '1px solid #2563eb', backgroundColor: '#eff6ff' };
const emptyCellStyle = { minHeight: '90px' };
const dayNumberStyle = { fontSize: '12px', fontWeight: 600, color: '#94a3b8' };
const dayPriceStyle = { fontSize: '11px', fontWeight: 600, color: '#475569' };
const priceBoxStyle = { marginTop: '16px', padding: '12px 14px', borderRadius: '10px', backgroundColor: '#f8fafc', border: '1px solid #e2e8f0' };
const priceRowStyle = { display: 'flex', justifyContent: 'space-between', fontSize: '14px', color: '#1e293b', padding: '2px 0' };
const priceNoteStyle = { fontSize: '12px', color: '#64748b', marginTop: '6px' };
const overlayStyle = { position: 'fixed', inset: 0, backgroundColor: 'rgba(15, 23, 42, 0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px', zIndex: 1000 };
const modalStyle = { backgroundColor: 'white', borderRadius: '16px', padding: '25px', width: '100%', maxWidth: '640px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 40px rgba(15, 23, 42, 0.2)', boxSizing: 'border-box' };
const closeButtonStyle = { display: 'flex', alignItems: 'center', justifyContent: 'center', width: '32px', height: '32px', borderRadius: '8px', border: '1px solid #e2e8f0', backgroundColor: 'white', cursor: 'pointer', color: '#334155', flexShrink: 0 };
const detailsGridStyle = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '14px' };
const detailLabelStyle = { fontSize: '12px', color: '#64748b', fontWeight: 600, marginBottom: '3px' };
const linkStyle = { color: '#2563eb', textDecoration: 'none' };
const primaryButtonStyle = { backgroundColor: '#2563eb', color: 'white', border: 'none', borderRadius: '8px', padding: '8px 16px', cursor: 'pointer', fontSize: '14px', fontWeight: 600 };
const saveButtonStyle = { backgroundColor: '#059669', color: 'white', border: 'none', borderRadius: '8px', padding: '8px 16px', cursor: 'pointer', fontSize: '14px', fontWeight: 600 };
const secondaryButtonStyle = { backgroundColor: '#f1f5f9', color: '#334155', border: 'none', borderRadius: '8px', padding: '8px 16px', cursor: 'pointer', fontSize: '14px' };

export default PlanningPage;
